/**
 * Authentication error types and interfaces for token refresh operations
 */

/**
 * Enum representing different types of token refresh errors
 */
export enum TokenRefreshErrorType {
  /** Refresh token has expired and cannot be used */
  EXPIRED_REFRESH_TOKEN = 'EXPIRED_REFRESH_TOKEN',
  /** No refresh token found in storage */
  NO_REFRESH_TOKEN = 'NO_REFRESH_TOKEN',
  /** Network error occurred during refresh attempt */
  NETWORK_ERROR = 'NETWORK_ERROR',
  /** Invalid response received from Keycloak server */
  INVALID_RESPONSE = 'INVALID_RESPONSE',
  /** Keycloak returned an error response */
  KEYCLOAK_ERROR = 'KEYCLOAK_ERROR',
}

/**
 * Detailed error information for token refresh failures
 */
export interface TokenRefreshError {
  /** Type of error that occurred */
  type: TokenRefreshErrorType;
  /** Technical error message for logging */
  message: string;
  /** User-friendly message in Portuguese for display */
  userMessage: string;
  /** Additional error details (optional) */
  details?: any;
}

/**
 * User profile data included in refresh response
 */
export interface RefreshUserProfile {
  id: string;
  username: string;
  email: string;
  firstName?: string;
  lastName?: string;
  roles: string[];
  groups: string[];
  allowedLayers: string[];
  allowedFeatures: string[];
  isAdmin: boolean;
  hasWildcard: boolean;
}

/**
 * Result of a token refresh operation
 */
export interface TokenRefreshResult {
  /** Whether the refresh operation succeeded */
  success: boolean;
  /** Token data if successful */
  data?: {
    access_token: string;
    refresh_token: string;
    user?: RefreshUserProfile;
  };
  /** Error information if failed */
  error?: TokenRefreshError;
}

/**
 * Result of token validation
 */
export interface TokenValidationResult {
  /** Whether the token is currently valid */
  isValid: boolean;
  /** Whether the token is expired */
  isExpired: boolean;
  /** Token expiration timestamp in milliseconds (optional) */
  expiresAt?: number;
  /** Error message if validation failed (optional) */
  error?: string;
}
